import React, {createContext, useContext, useEffect, useMemo, useState} from 'react'
import {yupResolver} from '@hookform/resolvers/yup'
import {useForm} from 'react-hook-form'
import {useHistory} from 'react-router'
import {getAndSetCityList} from 'Src/actions/RegistrationActions'
import {getDataForSoftPullEndpoint} from 'Src/components/pages/SoftPull/PageContextHelpers'
import {PageContextValue, PageProviderProps} from 'Src/components/pages/SoftPull/PageContextTypes'
import {useCustomerDispatchContext, useCustomerStateContext} from 'Src/contexts/CustomerContext'
import {useDealDispatchContext} from 'Src/contexts/DealContext'
import {useFrameContext} from 'Src/contexts/FrameContext'
import {useRegistrationContext} from 'Src/contexts/RegistrationContext'
import {useDebounce} from 'Src/utilities/CustomHooks'
import {displayPhoneNumber} from 'Src/utilities/formatters'
import {trans, transString} from 'Src/utilities/Helpers'
import useConfig from 'Src/utilities/useConfig'
import {SoftPullFormSchema} from 'Src/validation-schemas/SoftPullFormSchema'

export const INPUT_NAMES = {
  ADDRESS: 'address',
  CITY_COUNTY: 'cityCounty',
  EMAIL: 'email',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  LEGAL_CHECKBOX: 'legalCheckbox',
  PHONE: 'phone',
  STATE: 'state',
  ZIP_CODE: 'zipCode',
} as const

const PageContext = createContext<PageContextValue | undefined>(undefined)

export const PageProvider: React.FC<PageProviderProps> = ({value, children}) => {
  const pageHeading = transString('Get Pre-Qualified')
  const customerDispatch = useCustomerDispatchContext()
  const dealDispatch = useDealDispatchContext()
  const {customerData: customer} = useCustomerStateContext()
  const {registrationState, registrationDispatch} = useRegistrationContext()
  const {newLead, cityListOptions} = registrationState
  const zipCodePlaceHolder = trans('zip_code_enter')
  const cityPlaceHolder = trans('city_select')
  const [cityListPlaceholder, setCityListPlaceholder] = useState(zipCodePlaceHolder)
  const {a2zApi} = useFrameContext()
  const {registrationForm: {phoneRequired} = {phoneRequired: false}} = useConfig()
  const history = useHistory()

  const form = useForm({
    mode: 'onBlur',
    resolver: yupResolver(SoftPullFormSchema({phoneRequired})),
    defaultValues: {
      legalCheckbox: false,
      ...customer,
      address: customer?.address?.address ?? '',
      phone: displayPhoneNumber({phoneNumber: customer?.phone}),
    },
  })

  const {
    control,
    formState: {errors},
    watch,
    handleSubmit,
    setValue,
  } = form

  const watchZipCode = watch(INPUT_NAMES.ZIP_CODE, '')
  const watchCityCounty = watch(INPUT_NAMES.CITY_COUNTY, '')

  // Auto-populate "State" value based on the "City" selected.
  useEffect(() => {
    setValue(INPUT_NAMES.STATE, watchCityCounty.state ?? '')
  }, [watchCityCounty])

  useDebounce(
    async () => {
      if (!watchZipCode) {
        return
      }

      await getAndSetCityList(
        a2zApi,
        watchZipCode,
        registrationDispatch,
        registrationState.cityListZipCode
      )
    },
    250,
    [watchZipCode]
  )

  useEffect(() => {
    switch (cityListOptions?.length || 0) {
      case 0:
        setCityListPlaceholder(zipCodePlaceHolder)
        setValue(INPUT_NAMES.CITY_COUNTY, '')
        break
      case 1:
        setValue(INPUT_NAMES.CITY_COUNTY, cityListOptions[0].value)
        break

      default: {
        const cityCountyOption =
          cityListOptions.find((option) => option?.value?.id === customer?.cityCounty?.id) || ''
        setValue(INPUT_NAMES.CITY_COUNTY, cityCountyOption.value)
        setCityListPlaceholder(cityPlaceHolder)
        break
      }
    }
  }, [cityListOptions])

  /**
   * Existing function copied from src/components/Registration/RegistrationForm.jsx
   */
  const findMatchingLeads = (formData) => {
    const queryParams = [
      `filter[last_name]=${encodeURIComponent(formData.lastName)}`,
      `filter[email]=${encodeURIComponent(formData.email)}`,
      `include=customer.phones`,
    ].join('&')

    return a2zApi
      .get(`/customers/leads/verify?${queryParams}`)
      .then((response) => response.data)
      .then(({data, included}) =>
        data.map((lead) => {
          const includedCustomer = included.find(
            ({type, id}) => type === 'customers' && id === `${lead.attributes.customer_id}`
          )
          const includedCustomerPhone = included.find(
            ({type, id}) =>
              type === 'customerPhones' &&
              id === `${includedCustomer.relationships?.phones?.data[0]?.id}`
          )

          return {
            id: includedCustomer.id,
            leadId: lead.id,
            ...includedCustomer.attributes,
            phone: includedCustomerPhone?.attributes.number,
          }
        })
      )
  }

  /**
   * Existing function copied from src/components/Registration/RegistrationForm.jsx
   */
  const onSubmit = async (data) => {
    dealDispatch({type: 'update', payload: {loaded: false}})
    customerDispatch({
      type: 'setCustomerData',
      payload: {
        ...data,
        address: {
          address: data.address,
        },
      },
    })
    await customerDispatch(getDataForSoftPullEndpoint({data}))

    findMatchingLeads(data).then(async (foundLeads) => {
      if (foundLeads.length >= 1) {
        registrationDispatch({
          type: 'setFoundLeads',
          payload: foundLeads,
        })

        history.push('/register/matching-lead')
      } else if (!newLead) {
        // someone thought they were an existing lead, but no leads were found
        registrationDispatch({type: 'setNewLead', payload: true})
        registrationDispatch({type: 'setSkipMatchingLead', payload: true})

        history.push('/register')
      } else {
        history.push('/register/new-lead-confirmation')
      }
    })
  }

  const valueMemoized = useMemo(
    () => ({
      ...value,
      cityListPlaceholder,
      control,
      errors,
      handleSubmit,
      onSubmit,
      pageHeading,
      phoneRequired,
    }),
    [value, cityListPlaceholder, control, errors, pageHeading, phoneRequired]
  )

  return <PageContext.Provider value={valueMemoized}>{children}</PageContext.Provider>
}

export const usePageContext = (): PageContextValue => {
  const context = useContext(PageContext)

  if (!context) {
    throw new Error('usePageContext must be used within a PageProvider')
  }

  return context
}
